import {ChangeDetectionStrategy, Component, Inject, Injectable, signal, Signal} from "@angular/core";
import {DokumentId, DokumentSignatarerModell, DokumentVersjonModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Observable} from "rxjs";
import {toSignal} from "@angular/core/rxjs-interop";
import {DokumentSigneringDatatjeneste} from "felles-ng2/tjenester/datalager/DokumentSigneringDatatjeneste";
import {tomDokumentSignatarerModell} from "felles-ng2/modell/tommeModeller";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatToolbar} from "@angular/material/toolbar";
import {TranslateModule} from "@ngx-translate/core";
import {DokumentRedigerTilSigneringDialogRespons} from "../../../dokument/modale/dokument-rediger-til-signering-dialog/dokument-rediger-til-signering-dialog";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {SigneringStatusKomponent} from "../../komponenter/signering-status/signering-status";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";

const debug = initDebugger('SigneringAvsluttDialogKomponent');

/**
 * Dialog for å avslutte signeringsprosessen
 */
@Component({
    selector: 'signering-avslutt-dialog',
    templateUrl: 'signering-avslutt-dialog.html',
    styleUrl: 'signering-avslutt-dialog.scss',
    imports: [
        MatIcon,
        MatIconButton,
        MatToolbar,
        TranslateModule,
        MatDialogContent,
        TipsKomponent,
        SigneringStatusKomponent,
        MatButton,
        MatDialogActions,
        VisSpinnerDirektiv
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SigneringAvsluttDialogKomponent {

    static vis(dialog: MatDialog, dokumentversjon: DokumentVersjonModell) {
        return dialog.open<SigneringAvsluttDialogKomponent, DialogParametre, DokumentRedigerTilSigneringDialogRespons>(SigneringAvsluttDialogKomponent,
          {
              data: {dokumentversjon} as DialogParametre,
              disableClose: true,
              ariaLabelledBy: 'signering-avslutt-dialog-tittel',
              maxWidth: 600
          });
    }

    dokumentversjon: DokumentVersjonModell; //settes via DialogParametre

    modus = signal<'vis' | 'kvittering'>('vis');
    signatarer: Signal<DokumentSignatarerModell>;

    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<SigneringAvsluttDialogKomponent, SigneringAvsluttDialogRespons>,
                private avhengigheter: SigneringAvsluttDialogKomponentAvhengigheter) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());

        this.dokumentversjon = parametre.dokumentversjon;

        this.signatarer = toSignal(this.avhengigheter.signatarer(this.dokumentversjon.dokumentId), {initialValue: tomDokumentSignatarerModell()});
    }

    async avsluttSignering() {
        await this.lagrer.utfør(() => this.avhengigheter.markerSomFerdigSignert(this.dokumentversjon.dokumentId));
        this.modus.set('kvittering');
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }
}


export type SigneringAvsluttDialogRespons = boolean;

export type DialogParametre = { dokumentversjon: DokumentVersjonModell; };


@Injectable()
export class SigneringAvsluttDialogKomponentAvhengigheter {

    constructor(private dokumentSigneringDatatjeneste: DokumentSigneringDatatjeneste) {

    }

    signatarer(id: DokumentId): Observable<DokumentSignatarerModell> {
        return this.dokumentSigneringDatatjeneste.hent(id);
    }

    async markerSomFerdigSignert(id: DokumentId): Promise<void> {
        await this.dokumentSigneringDatatjeneste.markerSomFerdigSignert(id);
    }

}
